// ── translate.js — 한→중 번역 클라이언트 (3단 조회) ────────────────────────
//
// 조회 순서:
//   1) 메모리 캐시        — 같은 세션 내 재조회 0원
//   2) 고정 용어집        — src/i18n/glossary.js (288개, API 안 탐)
//   3) Firestore 공용캐시 — translations/{key}, 전 매장 공용
//   4) Claude Haiku       — 여기까지 와야 비로소 API 호출
//
// 그래서 "양파"는 셈핏 전체에서 평생 한 번만 번역된다.
//
// 설계 원칙:
// - 한국어 이름이 원본 키다. 중국어는 표시용 부가 필드(name_zh)일 뿐이다.
//   레시피 재료 매칭·OCR fuzzy 매칭·재고/발주 집계가 전부 한국어 name을 쓰므로
//   절대 한국어를 중국어로 "치환"하면 안 된다.
// - 사람이 고친 번역(source:'manual')은 AI가 덮어쓰지 않는다.
// - 네트워크가 죽으면 조용히 실패한다. 중국어가 안 뜰 뿐 앱은 정상 동작.
//   (허페이 매장은 VPN 경유라 끊기는 상황을 기본 전제로 둔다)

import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../db';
import { lookupGlossary, GLOSSARY } from '../i18n/glossary';
import { specToZh, unitToZh, hasHangul } from '../i18n/units';

const COLLECTION = 'translations';
const BATCH_SIZE = 50;
const SENTENCE_BATCH = 8;   // V109: 레시피 메모 같은 문장용

// 세션 메모리 캐시 — { "item:양파": "洋葱" }
const memCache = new Map();

// ── Firestore 문서 ID 생성 ────────────────────────────────────────────────
// Firestore 규칙: '/' 불가, 1500바이트 이하, '__...__' 형태 불가.
// 한글은 그대로 써도 되므로 가독성을 위해 원문을 살린다.
// V109: 레시피 메모처럼 긴 문장은 앞 120자가 같으면 서로 다른 글이 한 캐시를
// 덮어쓴다. 120자를 넘으면 전체 글의 해시를 붙여 구분한다.
// (단가표 품목명은 전부 120자 미만이라 기존 캐시 키는 하나도 안 바뀐다)
function shortHash(str) {
  let h = 5381;
  for (let i = 0; i < str.length; i++) h = ((h << 5) + h + str.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}
function cacheKey(kind, ko) {
  const norm = String(ko)
    .trim()
    .replace(/\//g, '／')      // 슬래시만 전각으로 치환
    .replace(/\s+/g, ' ');
  if (norm.length <= 120) return `${kind}:${norm}`;
  return `${kind}:${norm.slice(0, 110)}~${shortHash(norm)}`;
}

// ── V109: 문장 속 용어 추출 ──────────────────────────────────────────────
// 레시피 메모는 문장이라 용어집 '정확히 일치' 조회로는 안 걸린다.
// 대신 문장 안에 등장하는 용어집 단어를 골라 API에 "이렇게 번역하라"고 같이 보낸다.
// → "대파"가 문장마다 大葱/葱/青葱으로 흔들리지 않는다.
// 한 글자 용어(무·파·체 등)는 '전체'·'체크'처럼 엉뚱하게 걸리므로 제외.
const TERM_KEYS = Object.keys(GLOSSARY)
  .filter(k => k.replace(/\s+/g, '').length >= 2)
  .sort((a, b) => b.length - a.length);   // 긴 말 먼저 — "체에 거르다"가 "거르다"보다 우선

function collectTerms(texts, max = 60) {
  const joined = (texts || []).join('\n');
  if (!joined) return null;
  const squished = joined.replace(/\s+/g, '');
  const out = {};
  let n = 0;
  for (const k of TERM_KEYS) {
    if (n >= max) break;
    if (joined.includes(k) || squished.includes(k.replace(/\s+/g, ''))) {
      out[k] = GLOSSARY[k];
      n++;
    }
  }
  return n > 0 ? out : null;
}

const SENTENCE_KINDS = new Set(['recipe', 'text']);

// ── 1~2단계: 메모리 + 용어집 ─────────────────────────────────────────────
function localLookup(kind, ko) {
  const mk = `${kind}:${ko}`;
  if (memCache.has(mk)) return memCache.get(mk);
  const g = lookupGlossary(ko);
  if (g) {
    memCache.set(mk, g);
    return g;
  }
  return null;
}

// ── 3단계: Firestore 공용 캐시 읽기 ──────────────────────────────────────
async function readCache(kind, ko) {
  try {
    const snap = await getDoc(doc(db, COLLECTION, cacheKey(kind, ko)));
    if (!snap.exists()) return null;
    const zh = snap.data()?.zh;
    if (typeof zh === 'string' && zh.trim()) {
      // V114: 예전 규칙으로 캐시된 반쯤 번역된 업체명("청도식品")은 무시 → 다시 번역해서 덮어쓴다.
      // 사람이 직접 고친 값(manual)은 한글이 있어도 존중한다.
      if (kind === 'vendor' && /[가-힣]/.test(zh) && snap.data()?.source !== 'manual') return null;
      memCache.set(`${kind}:${ko}`, zh);
      return zh;
    }
  } catch (e) {
    // 권한·네트워크 문제 — 조용히 넘어간다
    console.debug('[translate] 캐시 읽기 실패:', e?.message);
  }
  return null;
}

// ── 캐시 쓰기 ─────────────────────────────────────────────────────────────
// manual: 사람이 직접 고친 번역. 이후 AI가 덮어쓰지 못하게 잠근다.
async function writeCache(kind, ko, zh, source = 'ai') {
  if (!zh || !String(zh).trim()) return;
  const ref = doc(db, COLLECTION, cacheKey(kind, ko));
  try {
    if (source === 'ai') {
      // 기존 문서가 manual이면 건드리지 않는다
      const snap = await getDoc(ref);
      if (snap.exists() && snap.data()?.source === 'manual') return;
    }
    await setDoc(ref, {
      ko: String(ko).trim(),
      zh: String(zh).trim(),
      kind,
      source,
      updatedAt: serverTimestamp(),
    }, { merge: true });
    memCache.set(`${kind}:${ko}`, String(zh).trim());
  } catch (e) {
    console.debug('[translate] 캐시 쓰기 실패:', e?.message);
  }
}

// ── 4단계: Haiku 호출 (429/529 자동 재시도) ──────────────────────────────
async function callApi(items, kind, attempt = 0, terms = null, dir = 'ko2zh') {
  try {
    const body = { items, kind };
    if (terms) body.terms = terms;
    if (dir === 'zh2ko') body.dir = 'zh2ko';   // V161 중국어 → 한국어
    const res = await fetch('/api/translate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (res.status === 429 || res.status === 529) {
      if (attempt < 3) {
        const wait = 1200 * Math.pow(2, attempt);   // 1.2s → 2.4s → 4.8s
        await new Promise(r => setTimeout(r, wait));
        return callApi(items, kind, attempt + 1, terms, dir);
      }
      return {};
    }
    if (!res.ok) return {};

    const data = await res.json();
    return data?.translations || {};
  } catch (e) {
    console.debug('[translate] API 호출 실패:', e?.message);
    return {};
  }
}

// ── 공개 API ──────────────────────────────────────────────────────────────

/**
 * 여러 개를 한 번에 번역한다. 캐시에 있는 건 API를 타지 않는다.
 * @param {string[]} koList 한국어 원문 배열
 * @param {'item'|'menu'|'vendor'|'text'|'recipe'} kind
 * @param {(done:number,total:number)=>void} [onProgress] 진행률 콜백 (일괄 번역 UI용)
 * @returns {Promise<Map<string,string>>} 한국어 → 중국어
 */
export async function translateMany(koList, kind = 'item', onProgress) {
  const result = new Map();
  const uniq = [...new Set(
    (koList || []).map(s => String(s || '').trim()).filter(Boolean)
  )];
  if (uniq.length === 0) return result;

  const total = uniq.length;
  let done = 0;
  const report = () => { if (typeof onProgress === 'function') onProgress(done, total); };

  // V122: 한글이 하나도 없으면(MSG, 1:1, 이미 중국어로 쓴 이름 등) 번역할 게 없다 → 그대로 쓴다.
  //       예전엔 서버가 '원문과 같은 번역'을 버려서, 이런 칸은 일괄 채우기를 눌러도 영영 빈 칸이었다.
  const needLookup = [];
  for (const ko of uniq) {
    if (!/[가-힣ㄱ-ㅎㅏ-ㅣ]/.test(ko)) { result.set(ko, ko); done++; }
    else needLookup.push(ko);
  }

  // 1~2단계
  const needCache = [];
  for (const ko of needLookup) {
    const hit = localLookup(kind, ko);
    if (hit) { result.set(ko, hit); done++; }
    else needCache.push(ko);
  }
  report();

  // 3단계 — Firestore 캐시 (병렬)
  const needApi = [];
  const cacheHits = await Promise.all(needCache.map(ko => readCache(kind, ko)));
  needCache.forEach((ko, i) => {
    if (cacheHits[i]) { result.set(ko, cacheHits[i]); done++; }
    else needApi.push(ko);
  });
  report();

  // 4단계 — API (단어는 50개씩, 문장은 8개씩)
  // 문장은 응답 JSON에 한국어 원문이 키로 되돌아오므로 50개를 한 번에 보내면
  // 출력 토큰 한도에 걸려 JSON이 잘리고 그 묶음 전체가 실패한다.
  const size = SENTENCE_KINDS.has(kind) ? SENTENCE_BATCH : BATCH_SIZE;
  for (let i = 0; i < needApi.length; i += size) {
    const chunk = needApi.slice(i, i + size);
    const terms = SENTENCE_KINDS.has(kind) ? collectTerms(chunk) : null;
    const translations = await callApi(chunk, kind, 0, terms);
    for (const ko of chunk) {
      const zh = translations[ko];
      if (zh) {
        result.set(ko, zh);
        writeCache(kind, ko, zh, 'ai');   // await 안 함 — 화면을 막지 않는다
      }
      done++;
    }
    report();
  }

  return result;
}

/**
 * 하나만 번역한다. 실패하면 null (한국어만 표시됨).
 */
export async function translateOne(ko, kind = 'item') {
  const raw = String(ko || '').trim();
  if (!raw) return null;
  const m = await translateMany([raw], kind);
  return m.get(raw) || null;
}

/**
 * 사람이 고친 번역을 저장한다. 이후 AI가 덮어쓰지 못한다.
 */
export async function saveManualTranslation(ko, zh, kind = 'item') {
  await writeCache(kind, ko, zh, 'manual');
}

/**
 * 단가표 배열에 name_zh를 채운다.
 * 이미 값이 있는 행은 건드리지 않는다 (사람이 고쳤을 수 있으므로).
 * @param {Array} prices 단가표
 * @param {object} [opts] { force: boolean, onProgress: fn }
 * @returns {Promise<{ next: Array, filled: number }>}
 */
export async function fillPriceNamesZh(prices, opts = {}) {
  const { force = false, onProgress } = opts;
  const list = Array.isArray(prices) ? prices : [];

  const targets = list
    .filter(p => p?.name && (force || !p.name_zh))
    .map(p => p.name);

  if (targets.length === 0) return { next: list, filled: 0 };

  const map = await translateMany(targets, 'item', onProgress);

  let filled = 0;
  const next = list.map(p => {
    if (!p?.name) return p;
    if (!force && p.name_zh) return p;
    const zh = map.get(String(p.name).trim());
    if (!zh) return p;
    filled++;
    return { ...p, name_zh: zh };
  });

  return { next, filled };
}

/**
 * V109: 레시피의 메뉴명·메모 중국어 중 비어 있는 것만 채운다.
 * 저장은 이미 끝난 뒤 백그라운드로 부른다 — 실패하면 null, 한국어만 보일 뿐.
 * 이미 값이 있는 칸은 건드리지 않는다 (사람이 고쳤을 수 있으므로).
 * @param {object} recipe { name, name_zh, note, note_zh }
 * @returns {Promise<{name_zh?:string, note_zh?:string}|null>} 채울 값만 담은 패치
 */
export async function fillRecipeZh(recipe) {
  if (!recipe) return null;
  const name = String(recipe.name || '').trim();
  const note = String(recipe.note || '').trim();
  const needName = !!name && !String(recipe.name_zh || '').trim();
  const needNote = !!note && !String(recipe.note_zh || '').trim();
  if (!needName && !needNote) return null;

  const patch = {};
  const [nameMap, noteMap] = await Promise.all([
    needName ? translateMany([name], 'menu') : Promise.resolve(null),
    needNote ? translateMany([note], 'recipe') : Promise.resolve(null),
  ]);
  const nz = nameMap?.get(name);
  const tz = noteMap?.get(note);
  if (nz) patch.name_zh = nz;
  if (tz) patch.note_zh = tz;
  return Object.keys(patch).length ? patch : null;
}

// ── V112: 단가표 행의 중국어 부가 필드 ─────────────────────────────────────
// 품목명(name_zh)뿐 아니라 제조사(maker_zh)·발주처(vendor_zh)·규격(spec_zh)·단위(unit_zh)까지.
// 규격·단위는 units.js 고정 표로 먼저 바꾸고, 표로 안 되는 것만 AI로 보낸다.
// 업체명은 한글이 있을 때만 번역한다 (중국 업체를 중국어로 적었으면 그대로 둔다).
export const PRICE_ZH_FIELDS = [
  ['name', 'name_zh'], ['maker', 'maker_zh'], ['vendor', 'vendor_zh'], ['spec', 'spec_zh'], ['unit', 'unit_zh'],
];

const str = v => String(v == null ? '' : v).trim();

/** V114: 업체명 중국어로 쓸 수 있는 값인가 — 비었거나 한글이 섞이면 아니다 */
export function validCompanyZh(zh) {
  const z = str(zh);
  return !!z && !hasHangul(z);
}

/** 이 행에서 AI 번역이 필요한 필드 → 한국어 원문 */
export function priceRowZhNeeds(p) {
  const need = {};
  if (!p) return need;
  if (str(p.name) && !str(p.name_zh)) need.name = str(p.name);
  // V114: 한글이 섞인 업체명 중국어("청도식品")는 잘못된 번역 → 비어 있는 것으로 보고 다시 채운다
  if (str(p.maker) && !validCompanyZh(p.maker_zh) && hasHangul(p.maker)) need.maker = str(p.maker);
  if (str(p.vendor) && !validCompanyZh(p.vendor_zh) && hasHangul(p.vendor)) need.vendor = str(p.vendor);
  if (str(p.spec) && !str(p.spec_zh) && hasHangul(p.spec) && !specToZh(p.spec)) need.spec = str(p.spec);
  if (str(p.unit) && !str(p.unit_zh) && hasHangul(p.unit) && !unitToZh(p.unit)) need.unit = str(p.unit);
  return need;
}

/**
 * 단가표 배열의 비어 있는 중국어 부가 필드를 채운다. 이미 값이 있는 칸은 건드리지 않는다.
 * @returns {Promise<{ next: Array, filled: number }>} filled = 바뀐 행 수
 */
export async function fillPriceZh(prices, opts = {}) {
  const { onProgress } = opts;
  const list = Array.isArray(prices) ? prices : [];
  const needs = list.map(priceRowZhNeeds);

  const names = [], companies = [], texts = [];
  for (const n of needs) {
    if (n.name) names.push(n.name);
    if (n.maker) companies.push(n.maker);
    if (n.vendor) companies.push(n.vendor);
    if (n.spec) texts.push(n.spec);
    if (n.unit) texts.push(n.unit);
  }
  const total = new Set(names).size + new Set(companies).size + new Set(texts).size;
  if (total === 0) return { next: list, filled: 0 };

  let doneBase = 0;
  const prog = (offset) => (d) => { if (typeof onProgress === 'function') onProgress(offset + d, total); };
  const nameMap = names.length ? await translateMany(names, 'item', prog(doneBase)) : new Map();
  doneBase += new Set(names).size;
  const compMap = companies.length ? await translateMany(companies, 'vendor', prog(doneBase)) : new Map();
  doneBase += new Set(companies).size;
  const textMap = texts.length ? await translateMany(texts, 'text', prog(doneBase)) : new Map();

  let filled = 0;
  const next = list.map((p, i) => {
    const n = needs[i];
    if (!p || !Object.keys(n).length) return p;
    const out = { ...p };
    let changed = false;
    const put = (field, map) => {
      const zh = n[field] && map.get(n[field]);
      if (zh) { out[field + '_zh'] = zh; changed = true; }
    };
    put('name', nameMap); put('maker', compMap); put('vendor', compMap); put('spec', textMap); put('unit', textMap);
    if (changed) filled++;
    return out;
  });
  return { next, filled };
}


// ── V161: 중국어 → 한국어 ─────────────────────────────────────────────────────
// 사장님: "중국인 직원한테 입력을 맡길 거라 중국어로 품목을 입력할 거야. 그럼 한국어가 자동으로"
//   품목 이름: 용어집(거꾸로) → 메모리 → Firestore 공용 캐시('item2ko:上海青' → { ko, zh }) → API(dir: zh2ko)
//   캐시 문서 모양은 한→중과 같다 (ko = 한국어, zh = 중국어) — 보안 규칙 그대로 통과
const REV_GLOSSARY = (() => {
  const m = new Map();
  for (const [ko, zh] of Object.entries(GLOSSARY)) {
    for (const z of String(zh || '').split(/[\/／,，]/).map(x => x.trim()).filter(Boolean)) if (!m.has(z)) m.set(z, ko);
  }
  return m;
})();
const revKind = (kind) => `${kind}2ko`;

async function readCacheKo(kind, zh) {
  try {
    const snap = await getDoc(doc(db, COLLECTION, cacheKey(revKind(kind), zh)));
    if (!snap.exists()) return null;
    const ko = snap.data()?.ko;
    if (typeof ko === 'string' && /[가-힣]/.test(ko)) { memCache.set(`${revKind(kind)}:${zh}`, ko); return ko; }
  } catch (e) { console.debug('[translate] 캐시 읽기 실패:', e?.message); }
  return null;
}
async function writeCacheKo(kind, zh, ko) {
  if (!ko || !/[가-힣]/.test(ko)) return;
  memCache.set(`${revKind(kind)}:${zh}`, ko);
  if (String(zh).length >= 200) return;   // 보안 규칙: zh 200자 미만
  try {
    const ref = doc(db, COLLECTION, cacheKey(revKind(kind), zh));
    const snap = await getDoc(ref);
    if (snap.exists() && snap.data()?.source === 'manual') return;
    await setDoc(ref, { ko: String(ko).trim(), zh: String(zh).trim(), kind: revKind(kind), source: 'ai', updatedAt: serverTimestamp() }, { merge: true });
  } catch (e) { console.debug('[translate] 캐시 쓰기 실패:', e?.message); }
}

/**
 * 중국어 여러 개 → 한국어. 한글이 이미 있으면 그대로.
 * @returns {Promise<Map<string,string>>} 중국어 → 한국어 (못 찾은 건 빠짐)
 */
export async function translateManyToKo(zhList, kind = 'item') {
  const result = new Map();
  const uniq = [...new Set((zhList || []).map(x => String(x || '').trim()).filter(Boolean))];
  const need = [];
  for (const zh of uniq) {
    if (/[가-힣]/.test(zh)) { result.set(zh, zh); continue; }
    const hit = memCache.get(`${revKind(kind)}:${zh}`) || REV_GLOSSARY.get(zh);
    if (hit) result.set(zh, hit); else need.push(zh);
  }
  const hits = await Promise.all(need.map(zh => readCacheKo(kind, zh)));
  const needApi = need.filter((zh, i) => { if (hits[i]) { result.set(zh, hits[i]); return false; } return true; });
  const size = SENTENCE_KINDS.has(kind) ? SENTENCE_BATCH : BATCH_SIZE;
  for (let i = 0; i < needApi.length; i += size) {
    const chunk = needApi.slice(i, i + size);
    const tr = await callApi(chunk, kind, 0, null, 'zh2ko');
    for (const zh of chunk) {
      const ko = tr[zh];
      if (ko && /[가-힣]/.test(ko)) { result.set(zh, ko); writeCacheKo(kind, zh, ko); }
    }
  }
  return result;
}

export async function translateToKo(zh, kind = 'item') {
  const raw = String(zh || '').trim();
  if (!raw) return null;
  const m = await translateManyToKo([raw], kind);
  return m.get(raw) || null;
}

/**
 * 메모 같은 문장 — 한글이 있으면 중국어로, 없으면(중국어로 쓴 것) 한국어로
 * @returns {Promise<{ to: 'zh'|'ko', text: string } | null>}
 */
export async function translateMemo(text) {
  const raw = String(text || '').trim();
  if (!raw) return null;
  if (/[가-힣]/.test(raw)) {
    const zh = await translateOne(raw, 'text');
    return zh && zh !== raw ? { to: 'zh', text: zh } : null;
  }
  if (!/[\u3400-\u9fff]/.test(raw)) return null;
  const ko = await translateToKo(raw, 'text');
  return ko ? { to: 'ko', text: ko } : null;
}
